/**
 * The signed-in user's side-nav layout (pins, hidden items, collapsed
 * categories), saved with the other shell preferences so it follows the
 * user across devices, and the integration states for the offices in scope.
 */
import type { NavLayout } from '@velora/contracts/shell';
import { getShellIntegrations } from '@velora/sdk';
import { useCallback, useMemo } from 'react';
import { useOfficeQuery } from '../../office/useOfficeQuery.js';
import { usePrefs } from '../../prefs/prefs.js';
import type { ArrangeOptions } from './model.js';

export function useNavLayout(): [NavLayout, (next: NavLayout) => void] {
  const prefs = usePrefs();
  const { set } = prefs;
  const save = useCallback((next: NavLayout) => set({ navLayout: next }), [set]);
  return [prefs.navLayout, save];
}

export const INTEGRATIONS_KEY = ['shell', 'integrations'] as const;

/** Integration states by key; `undefined` while loading or after a failure (nothing moves). */
export function useIntegrations(): ArrangeOptions['integrations'] {
  const q = useOfficeQuery({
    queryKey: INTEGRATIONS_KEY,
    queryFn: (c, signal) => getShellIntegrations(c, { signal }),
    staleTime: 120_000,
    retry: false,
  });
  return useMemo(() => {
    if (!q.data) return undefined;
    return Object.fromEntries(
      q.data.integrations.map((i) => [
        i.key,
        {
          state: i.state,
          setupHref: i.setupHref,
          ...(i.detail ? { detail: i.detail } : {}),
        },
      ]),
    );
  }, [q.data]);
}
